// /src/components/layout/SidebarMenu.js

import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaHome,
  FaBuilding,
  FaComments,
  FaUserFriends,
  FaUserCircle,
  FaQuestionCircle,
  FaQrcode,
  FaSignOutAlt,
  FaCog,
  FaChartLine
} from 'react-icons/fa';

const MenuItem = ({ to, icon: Icon, children, description, isActive, onClick }) => {
  const itemClasses = `
    flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
    ${isActive 
      ? 'bg-indigo-600 text-white' 
      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
    }
  `;

  if (onClick) {
    return (
      <button 
        onClick={onClick}
        className={itemClasses}
        title={description}
      >
        <Icon className="h-5 w-5" />
        <span className="font-medium">{children}</span>
      </button>
    );
  }

  return (
    <Link
      to={to}
      className={itemClasses}
      title={description}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{children}</span>
    </Link>
  );
};

const SidebarMenu = ({ isAuthenticated, setIsAuthenticated, userRole }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  if (!isAuthenticated || location.pathname === '/') {
    return null;
  }

  const isPathActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const menuItems = [
    {
      roles: ['admin', 'user'],
      items: [
        {
          to: '/dashboard',
          icon: FaHome,
          titleKey: 'sidebar.navigation.dashboard.title',
          descriptionKey: 'sidebar.navigation.dashboard.description',
          isActive: location.pathname === '/dashboard'
        },
        {
          to: '/properties',
          icon: FaBuilding,
          titleKey: 'sidebar.navigation.properties.title',
          descriptionKey: 'sidebar.navigation.properties.description',
          isActive: isPathActive('/properties')
        },
        {
          to: '/chat',
          icon: FaComments,
          titleKey: 'sidebar.navigation.chat.title',
          descriptionKey: 'sidebar.navigation.chat.description',
          isActive: location.pathname === '/chat'
        },
        {
          to: '/faqs',
          icon: FaQuestionCircle,
          titleKey: 'sidebar.navigation.faq.title',
          descriptionKey: 'sidebar.navigation.faq.description',
          isActive: isPathActive('/faqs')
        },
        {
          to: '/qrcodes',
          icon: FaQrcode,
          titleKey: 'sidebar.navigation.qrcodes.title',
          descriptionKey: 'sidebar.navigation.qrcodes.description',
          isActive: isPathActive('/qrcodes')
        },
        {
          to: '/invitation-management',
          icon: FaUserFriends,
          titleKey: 'sidebar.navigation.invitations.title',
          descriptionKey: 'sidebar.navigation.invitations.description',
          isActive: location.pathname === '/invitation-management'
        }
      ]
    },
    {
      roles: ['admin'],
      items: [
        {
          to: '/user-management',
          icon: FaUserCircle,
          titleKey: 'sidebar.navigation.users.title',
          descriptionKey: 'sidebar.navigation.users.description',
          isActive: location.pathname === '/user-management'
        }
      ]
    },
    {
      roles: ['admin', 'user'],
      items: [
        {
          to: '/profile',
          icon: FaCog,
          titleKey: 'sidebar.navigation.profile.title',
          descriptionKey: 'sidebar.navigation.profile.description',
          isActive: location.pathname === '/profile'
        }
      ]
    }
  ];

  return (
    <div className="hidden md:flex flex-col h-screen bg-gray-900 w-64 border-r border-gray-800">
      {/* Logo Section */}
      <div className="px-6 py-8">
        <Link to="/" className="flex items-center">
          <span className="text-2xl font-bold text-white">
            {t('auth.common.brandName')}
          </span>
        </Link>
      </div>

      {/* Navigation Section */}
      <nav className="flex-1 px-4 space-y-2 overflow-y-auto">
        {menuItems.map((section, sectionIndex) => (
          section.roles.includes(userRole) && (
            <div key={sectionIndex} className="space-y-1">
              {section.items.map((item, itemIndex) => (
                <MenuItem
                  key={itemIndex}
                  to={item.to}
                  icon={item.icon}
                  isActive={item.isActive}
                  description={t(item.descriptionKey)}
                >
                  {t(item.titleKey)}
                </MenuItem>
              ))}
            </div>
          )
        ))}
      </nav>

      {userRole === 'guest' && (
        <div className="px-4 py-4 border-t border-gray-800">
          <p className="text-sm text-gray-400 mb-4">
            {t('sidebar.navigation.guestActions.description')}
          </p>
          <div className="space-y-2">
            <Link
              to="/login"
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                      transition-colors duration-200 flex items-center justify-center"
            >
              {t('sidebar.navigation.guestActions.login')}
            </Link>
            <Link
              to="/signup"
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 
                      transition-colors duration-200 flex items-center justify-center"
            >
              {t('sidebar.navigation.guestActions.signup')}
            </Link>
          </div>
        </div>
      )}

      {/* Footer Section */}
      <div className="px-4 py-4 space-y-4">

        {/* Version Info */}
        <div className="text-xs text-gray-500 text-center border-t border-gray-800 pt-4">
          {t('sidebar.footer.version')} 1.0.0
        </div>
      </div>
    </div>
  );
};

export default SidebarMenu;